<template>
  <div 
    class="diagnostics-panel" 
    :class="{ 'panel-open': isOpen, 'is-dragging': isDragging }"
    :style="panelStyle"
  >
    <!-- Header met toggle en drag -->
    <div 
      class="panel-header" 
      @click="handleHeaderClick"
      @mousedown="startDrag"
    >
      <div class="d-flex align-items-center gap-2">
        <i class="fas" :class="isOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
        <span class="panel-title">Diagnostics</span>
        <span class="badge bg-secondary" :title="`${data.validRecords} valide van ${data.totalRecords} totaal — verschil = records met ontbrekende of ongeldige coördinaten (zie tab 'Fouten')`">
          {{ data.validRecords }}/{{ data.totalRecords }} valide
        </span>
        <span class="badge bg-success" :title="'Aantal records dat momenteel zichtbaar is op de kaart na alle filters'">
          {{ data.visibleRecords ?? data.validRecords }} zichtbaar
        </span>
        <span v-if="errorCount > 0" class="badge bg-danger">{{ errorCount }} errors</span>
        <span v-if="overlapCount > 0" class="badge bg-warning text-dark">{{ overlapCount }} overlaps</span>
        <span v-if="warningCount > 0" class="badge bg-info text-dark">{{ warningCount }} warnings</span>
      </div>
    </div>

    <!-- Resize handles (buiten scrollbare content) -->
    <div 
      v-if="isOpen"
      class="resize-handle-left"
      @mousedown="startResizeWidth"
    ></div>
    <div 
      v-if="isOpen"
      class="resize-handle-bottom"
      @mousedown="startResizeHeight"
    ></div>
    <div 
      v-if="isOpen"
      class="resize-handle-corner"
      @mousedown="startResize"
    ></div>

    <!-- Panel content -->
    <div
      v-if="isOpen"
      ref="panelContent"
      class="panel-content"
      @scroll="onScroll"
    >
      <!-- Tabs navigatie -->
      <ul class="nav nav-tabs diagnostics-tabs">
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'filters' }"
            @click="setActiveTab('filters')"
          >
            <i class="fas fa-filter text-primary me-1"></i>
            Filters
            <span class="badge ms-1" :class="totalFilterHidden > 0 ? 'bg-primary' : 'bg-secondary'">{{ totalFilterHidden }}</span>
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'errors' }"
            @click="setActiveTab('errors')"
          >
            <i class="fas fa-exclamation-circle text-danger me-1"></i>
            Fouten
            <span class="badge ms-1" :class="errorCount > 0 ? 'bg-danger' : 'bg-secondary'">{{ errorCount }}</span>
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'warnings' }"
            @click="setActiveTab('warnings')"
          >
            <i class="fas fa-exclamation-triangle text-info me-1"></i>
            Waarschuwingen
            <span class="badge ms-1" :class="warningCount > 0 ? 'bg-info text-dark' : 'bg-secondary'">{{ warningCount }}</span>
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'overlaps' }"
            @click="setActiveTab('overlaps')"
          >
            <i class="fas fa-layer-group text-warning me-1"></i>
            Overlaps
            <span class="badge ms-1" :class="overlapCount > 0 ? 'bg-warning text-dark' : 'bg-secondary'">{{ overlapCount }}</span>
          </button>
        </li>
      </ul>

      <!-- Tab content - Errors -->
      <div v-show="activeTab === 'errors'" class="mt-2">
        <div v-if="errorCount === 0" class="text-muted small p-3 text-center">
          <i class="fas fa-check-circle text-success me-1"></i>
          Geen fouten gevonden
        </div>
        <div v-else class="table-container">
          <table class="table table-sm table-striped overlap-table">
            <thead>
              <tr>
                <th style="width: 20px;"></th>
                <th>ID</th>
                <th>Reden</th>
                <th v-if="data.formId">Actie</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="error in data.errors" :key="error.id">
                <tr
                  class="overlap-row"
                  :class="{ 'expanded': expandedErrors.includes(error.id) }"
                  @click="toggleRawData(error.id)"
                >
                  <td class="toggle-cell">
                    <i class="fas fa-chevron-right toggle-icon" :class="{ 'rotated': expandedErrors.includes(error.id) }"></i>
                  </td>
                  <td class="text-truncate" style="max-width: 100px;" :title="error.id">
                    {{ error.id.substring(0, 8) }}...
                  </td>
                  <td>
                    <span class="badge" :class="getErrorBadgeClass(error.reason)">
                      {{ formatErrorReason(error.reason) }}
                    </span>
                  </td>
                  <td v-if="data.formId">
                    <a
                      :href="getCrudLink([error.id])"
                      target="_blank"
                      class="btn btn-sm btn-outline-primary"
                      @click.stop
                      title="Open in CRUD pagina"
                    >
                      <i class="fas fa-external-link-alt"></i>
                    </a>
                  </td>
                </tr>
                <!-- Inline expanded raw data row -->
                <tr v-if="expandedErrors.includes(error.id)" class="expanded-row">
                  <td :colspan="data.formId ? 4 : 3" class="expanded-content">
                    <pre class="mb-0 raw-data-pre">{{ JSON.stringify(error.rawData, null, 2) }}</pre>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tab content - Waarschuwingen -->
      <div v-show="activeTab === 'warnings'" class="mt-2">
        <div v-if="warningCount === 0" class="text-muted small p-3 text-center">
          <i class="fas fa-check-circle text-success me-1"></i>
          Geen waarschuwingen
        </div>
        <div v-else>
          <div class="alert alert-light small mb-3 py-2">
            <i class="fas fa-info-circle text-info me-1"></i>
            Records komen wél door, maar er is iets niet helemaal compleet aan de marker-template-configuratie.
          </div>

          <!-- Ontbrekende template velden -->
          <div v-if="missingFieldWarnings.length > 0" class="warnings-section mb-3">
            <h6 class="section-title">
              <i class="fas fa-exclamation-triangle text-info me-1"></i>
              Ontbrekende template velden ({{ missingFieldWarnings.length }})
            </h6>
            <div class="small text-muted mb-2 ms-3">
              Veld is geconfigureerd op de marker, maar ontbreekt of is leeg in (een deel van) de records — het bijbehorende blokje blijft leeg op de marker.
            </div>
            <div class="table-container" style="max-height: 200px;">
              <table class="table table-sm table-striped overlap-table">
                <thead>
                  <tr>
                    <th style="width: 20px;"></th>
                    <th>Veld</th>
                    <th>Ontbreekt in</th>
                    <th v-if="data.formId">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="warning in missingFieldWarnings" :key="warning.field">
                    <tr
                      class="overlap-row"
                      :class="{ 'expanded': expandedWarnings.includes(warning.field) }"
                      @click="toggleWarningIds(warning.field)"
                    >
                      <td class="toggle-cell">
                        <i class="fas fa-chevron-right toggle-icon" :class="{ 'rotated': expandedWarnings.includes(warning.field) }"></i>
                      </td>
                      <td>
                        <code class="field-name">{{ warning.field }}</code>
                      </td>
                      <td>
                        <span class="badge bg-info text-dark">{{ warning.count }} records</span>
                      </td>
                      <td v-if="data.formId">
                        <a
                          :href="getCrudLink(warning.recordIds)"
                          target="_blank"
                          class="btn btn-sm btn-outline-primary"
                          @click.stop
                          title="Open in CRUD pagina"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </a>
                      </td>
                    </tr>
                    <tr v-if="expandedWarnings.includes(warning.field)" class="expanded-row">
                      <td :colspan="data.formId ? 4 : 3" class="expanded-content">
                        <div class="ids-list">
                          <span v-for="id in warning.recordIds.slice(0, 20)" :key="id" class="badge bg-light text-dark me-1 mb-1">
                            {{ id.substring(0, 8) }}...
                          </span>
                          <span v-if="warning.recordIds.length > 20" class="badge bg-secondary">
                            +{{ warning.recordIds.length - 20 }} meer
                          </span>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Icon mapping drift -->
          <div v-if="iconDriftWarnings.length > 0" class="warnings-section mb-3">
            <h6 class="section-title">
              <i class="fas fa-map-marker-alt text-warning me-1"></i>
              Icon mapping ontbreekt ({{ iconDriftWarnings.length }})
            </h6>
            <div class="small text-muted mb-2 ms-3">
              Records hebben een waarde waarvoor in de icon-mapping geen icoon is geconfigureerd — er wordt een fallback-icoon gebruikt.
            </div>
            <div class="table-container" style="max-height: 200px;">
              <table class="table table-sm table-striped overlap-table">
                <thead>
                  <tr>
                    <th style="width: 20px;"></th>
                    <th>Veld</th>
                    <th>Ongemapte waarden</th>
                    <th>Records</th>
                    <th v-if="data.formId">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="warning in iconDriftWarnings" :key="'drift-' + warning.field">
                    <tr
                      class="overlap-row"
                      :class="{ 'expanded': expandedWarnings.includes('drift-' + warning.field) }"
                      @click="toggleWarningIds('drift-' + warning.field)"
                    >
                      <td class="toggle-cell">
                        <i class="fas fa-chevron-right toggle-icon" :class="{ 'rotated': expandedWarnings.includes('drift-' + warning.field) }"></i>
                      </td>
                      <td>
                        <code class="field-name">{{ warning.field }}</code>
                      </td>
                      <td>
                        <span v-for="val in warning.unmappedValues" :key="val" class="badge bg-warning text-dark me-1">
                          {{ val }}
                        </span>
                      </td>
                      <td>
                        <span class="badge bg-warning text-dark">{{ warning.count }}</span>
                      </td>
                      <td v-if="data.formId">
                        <a
                          :href="getCrudLink(warning.recordIds)"
                          target="_blank"
                          class="btn btn-sm btn-outline-primary"
                          @click.stop
                          title="Open in CRUD pagina"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </a>
                      </td>
                    </tr>
                    <tr v-if="expandedWarnings.includes('drift-' + warning.field)" class="expanded-row">
                      <td :colspan="data.formId ? 5 : 4" class="expanded-content">
                        <div class="ids-list">
                          <span v-for="id in warning.recordIds.slice(0, 20)" :key="id" class="badge bg-light text-dark me-1 mb-1">
                            {{ id.substring(0, 8) }}...
                          </span>
                          <span v-if="warning.recordIds.length > 20" class="badge bg-secondary">
                            +{{ warning.recordIds.length - 20 }} meer
                          </span>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab content - Overlaps -->
      <div v-show="activeTab === 'overlaps'" class="mt-2">
        <div v-if="overlapCount === 0" class="text-muted small p-3 text-center">
          <i class="fas fa-check-circle text-success me-1"></i>
          Geen overlappende markers gevonden
        </div>
        <div v-else class="table-container">
          <table class="table table-sm table-striped overlap-table">
            <thead>
              <tr>
                <th style="width: 20px;"></th>
                <th>Positie</th>
                <th>Aantal</th>
                <th v-if="data.formId">Actie</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(overlap, index) in data.overlaps" :key="index">
                <tr 
                  class="overlap-row" 
                  :class="{ 'expanded': expandedOverlaps.includes(index) }"
                  @click="toggleOverlapIds(index)"
                >
                  <td class="toggle-cell">
                    <i class="fas fa-chevron-right toggle-icon" :class="{ 'rotated': expandedOverlaps.includes(index) }"></i>
                  </td>
                  <td class="small">
                    {{ overlap.position.lat.toFixed(4) }}, {{ overlap.position.lng.toFixed(4) }}
                  </td>
                  <td>
                    <span class="badge bg-warning text-dark">{{ overlap.count }}</span>
                  </td>
                  <td v-if="data.formId">
                    <a 
                      :href="getCrudLink(overlap.ids)"
                      target="_blank"
                      class="btn btn-sm btn-outline-primary"
                      @click.stop
                      title="Open in CRUD pagina"
                    >
                      <i class="fas fa-external-link-alt"></i>
                    </a>
                  </td>
                </tr>
                <!-- Inline expanded IDs row -->
                <tr v-if="expandedOverlaps.includes(index)" class="expanded-row">
                  <td colspan="4" class="expanded-content">
                    <div class="ids-list">
                      <span v-for="id in overlap.ids" :key="id" class="badge bg-light text-dark me-1 mb-1">
                        {{ id.substring(0, 8) }}...
                      </span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tab content - Filters -->
      <div v-show="activeTab === 'filters'" class="mt-2">
        <div v-if="totalFilterHidden === 0" class="text-muted small p-3 text-center">
          <i class="fas fa-check-circle text-success me-1"></i>
          Er zijn momenteel geen actieve filters die records verbergen op de kaart.
          Alle <strong>{{ data.validRecords }}</strong> valide records zijn zichtbaar.
        </div>
        <div v-else class="filter-summary p-2">
          <!-- Algemene uitleg -->
          <div class="alert alert-light small mb-3 py-2">
            <i class="fas fa-info-circle text-primary me-1"></i>
            <strong>{{ data.visibleRecords ?? data.validRecords }}</strong> van <strong>{{ data.validRecords }}</strong> records zichtbaar. Tellingen per filter zijn cumulatief.
          </div>

          <!-- Publicatie-filter -->
          <div v-if="data.pubFilter && data.pubFilterHidden > 0" class="filter-item mb-3 p-2 border rounded">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <i class="fas fa-eye-slash text-primary me-1"></i>
                <strong>Publicatie-filter</strong>
                <span class="text-muted small ms-1">({{ data.pubFilter.mode === 'exclude' ? 'uitsluitingsmodus' : 'inclusiemodus' }})</span>
              </div>
              <span class="badge bg-primary">{{ data.pubFilterHidden }} verborgen</span>
            </div>
            <div class="small text-muted ms-3 mt-1">
              Veld <code>{{ data.pubFilter.property }}</code> wordt vergeleken met waarde <code>{{ data.pubFilter.value }}</code>.
            </div>
            <div class="small ms-3 mt-1">
              <em>{{ data.pubFilter.mode === 'exclude'
                ? 'Records mét deze waarde worden verborgen — bedoeld om concept-/onafgeronde records buiten de kaart te houden.'
                : 'Alleen records mét deze waarde worden getoond — bedoeld om uitsluitend gepubliceerde records te tonen.' }}</em>
            </div>
          </div>

          <!-- Search filter -->
          <div v-if="data.searchHidden > 0" class="filter-item mb-3 p-2 border rounded">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <i class="fas fa-search text-info me-1"></i>
                <strong>Zoekopdracht</strong>
              </div>
              <span class="badge bg-info text-dark">{{ data.searchHidden }} verborgen</span>
            </div>
            <div v-if="data.searchQuery" class="small text-muted ms-3 mt-1">
              Records waarvan geen veldwaarde "<em>{{ data.searchQuery }}</em>" bevat, worden verborgen.
            </div>
          </div>

          <!-- Veld-filters -->
          <div v-for="filter in data.fieldFilters || []" :key="filter.field" class="filter-item mb-3 p-2 border rounded">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <i class="fas fa-sliders-h text-secondary me-1"></i>
                <strong>{{ filter.label }}</strong>
                <span class="text-muted small ms-1">(filter-type: {{ filter.type }})</span>
              </div>
              <span class="badge bg-secondary">{{ filter.hidden }} verborgen</span>
            </div>
            <div class="small text-muted ms-3 mt-1">
              Gebaseerd op het veld <code>{{ filter.field }}</code>.
            </div>
            <div class="small ms-3 mt-1">
              <em>{{ filterTypeExplanation(filter.type) }}</em>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DiagnosticsPanel',

  props: {
    data: {
      type: Object,
      required: true
    }
  },

  data() {
    // Laad opgeslagen staat uit localStorage
    const savedState = this.loadState();
    
    return {
      isOpen: savedState.isOpen ?? false,
      expandedErrors: savedState.expandedErrors ?? [],
      expandedOverlaps: savedState.expandedOverlaps ?? [],
      expandedWarnings: savedState.expandedWarnings ?? [],
      // Tab state - wordt later bepaald op basis van counts of localStorage
      activeTab: savedState.activeTab ?? null,
      // Resize state
      panelWidth: savedState.panelWidth ?? 600,
      panelHeight: savedState.panelHeight ?? 350,
      isResizing: false,
      resizeMode: null, // 'width', 'height', 'both'
      startX: 0,
      startY: 0,
      startWidth: 0,
      startHeight: 0,
      // Drag state
      isDragging: false,
      hasDragged: false,
      panelTop: savedState.panelTop ?? 10,
      panelRight: savedState.panelRight ?? 10,
      dragStartX: 0,
      dragStartY: 0,
      dragStartTop: 0,
      dragStartRight: 0,
      // Scroll state
      scrollTop: savedState.scrollTop ?? 0
    };
  },

  watch: {
    // Sla staat op bij wijzigingen
    isOpen(newVal) { 
      this.saveState(); 
      if (newVal) {
        this.restoreScrollPosition();
      }
    },
    panelWidth() { this.saveState(); },
    panelHeight() { this.saveState(); },
    panelTop() { this.saveState(); },
    panelRight() { this.saveState(); },
    activeTab() { this.saveState(); },
    expandedErrors: { deep: true, handler() { this.saveState(); } },
    expandedOverlaps: { deep: true, handler() { this.saveState(); } },
    expandedWarnings: { deep: true, handler() { this.saveState(); } },
    // Bepaal actieve tab wanneer data verandert
    data: {
      immediate: true,
      handler() {
        this.initializeActiveTab();
      }
    }
  },

  computed: {
    errorCount() {
      return this.data.errors?.length || 0;
    },
    overlapCount() {
      return this.data.overlaps?.length || 0;
    },
    warningCount() {
      return this.data.warnings?.length || 0;
    },
    totalFilterHidden() {
      const pub = this.data.pubFilterHidden || 0;
      const search = this.data.searchHidden || 0;
      const fieldTotal = (this.data.fieldFilters || []).reduce((sum, f) => sum + (f.hidden || 0), 0);
      return pub + search + fieldTotal;
    },
    missingFieldWarnings() {
      return (this.data.warnings || []).filter(w => w.type === 'missing_field' || !w.type);
    },
    iconDriftWarnings() {
      return (this.data.warnings || []).filter(w => w.type === 'icon_mapping_drift');
    },
    panelStyle() {
      const style = {
        top: this.panelTop + 'px',
        right: this.panelRight + 'px'
      };
      if (this.isOpen) {
        style.width = this.panelWidth + 'px';
        style.height = this.panelHeight + 'px';
      }
      return style;
    }
  },

  methods: {
    // LocalStorage persistence
    loadState() {
      try {
        const saved = localStorage.getItem('diagnosticsPanel');
        return saved ? JSON.parse(saved) : {};
      } catch (e) {
        return {};
      }
    },

    saveState() {
      try {
        const state = {
          isOpen: this.isOpen,
          panelWidth: this.panelWidth,
          panelHeight: this.panelHeight,
          panelTop: this.panelTop,
          panelRight: this.panelRight,
          activeTab: this.activeTab,
          expandedErrors: this.expandedErrors,
          expandedOverlaps: this.expandedOverlaps,
          expandedWarnings: this.expandedWarnings,
          scrollTop: this.scrollTop
        };
        localStorage.setItem('diagnosticsPanel', JSON.stringify(state));
      } catch (e) {
        // Ignore storage errors
      }
    },

    initializeActiveTab() {
      // Als er al een geldige tab geselecteerd is, behoud die
      if (
        this.activeTab === 'errors' ||
        this.activeTab === 'overlaps' ||
        this.activeTab === 'filters' ||
        this.activeTab === 'warnings'
      ) return;
      // Standaard: filters-tab als startpunt
      this.activeTab = 'filters';
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },

    onScroll(event) {
      this.scrollTop = event.target.scrollTop;
      this.saveState();
    },

    restoreScrollPosition() {
      this.$nextTick(() => {
        if (this.$refs.panelContent && this.scrollTop > 0) {
          this.$refs.panelContent.scrollTop = this.scrollTop;
        }
      });
    },

    filterTypeExplanation(type) {
      const map = {
        checkbox: 'Records die niet één van de aangevinkte opties bevatten worden verborgen.',
        select: 'Records waarvan de waarde niet overeenkomt met de geselecteerde optie worden verborgen.',
        radio: 'Records waarvan de waarde niet overeenkomt met de gekozen optie worden verborgen.',
        range: 'Records waarvan de waarde buiten het ingestelde bereik valt worden verborgen.',
      };
      return map[type] || 'Records die niet aan de filtercriteria voldoen worden verborgen.';
    },

    formatErrorReason(reason) {
      const reasonMap = {
        'missing_lat': 'Geen latitude',
        'missing_lng': 'Geen longitude',
        'invalid_lat': 'Ongeldige latitude',
        'invalid_lng': 'Ongeldige longitude',
        'missing_icon': 'Geen icon'
      };
      return reasonMap[reason] || reason;
    },

    getErrorBadgeClass(reason) {
      if (reason.includes('invalid')) return 'bg-danger';
      if (reason.includes('missing')) return 'bg-warning text-dark';
      return 'bg-secondary';
    },

    toggleRawData(errorId) {
      const index = this.expandedErrors.indexOf(errorId);
      if (index === -1) {
        this.expandedErrors.push(errorId);
      } else {
        this.expandedErrors.splice(index, 1);
      }
    },

    toggleOverlapIds(overlapIndex) {
      const index = this.expandedOverlaps.indexOf(overlapIndex);
      if (index === -1) {
        this.expandedOverlaps.push(overlapIndex);
      } else {
        this.expandedOverlaps.splice(index, 1);
      }
    },

    toggleWarningIds(fieldKey) {
      const index = this.expandedWarnings.indexOf(fieldKey);
      if (index === -1) {
        this.expandedWarnings.push(fieldKey);
      } else {
        this.expandedWarnings.splice(index, 1);
      }
    },

    getCrudLink(ids) {
      if (!this.data.formId || !this.data.adminOrigin) return '#';
      const selectParam = ids.join(',');
      return `${this.data.adminOrigin}/admin/form/${this.data.formId}/data?select=${selectParam}`;
    },

    // Header click - alleen toggle als we niet aan het slepen waren
    handleHeaderClick() {
      if (!this.hasDragged) {
        this.isOpen = !this.isOpen;
      }
      this.hasDragged = false;
    },

    // Drag methods
    startDrag(event) {
      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartTop = this.panelTop;
      this.dragStartRight = this.panelRight;
      
      document.addEventListener('mousemove', this.doDrag);
      document.addEventListener('mouseup', this.stopDrag);
      event.preventDefault();
    },

    doDrag(event) {
      if (!this.isDragging) return;
      
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      
      // Als we meer dan 5px bewogen hebben, markeer als gesleept
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        this.hasDragged = true;
      }
      
      // Right wordt kleiner als we naar rechts slepen (deltaX positief)
      this.panelRight = Math.max(0, this.dragStartRight - deltaX);
      this.panelTop = Math.max(0, this.dragStartTop + deltaY);
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.doDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    // Resize methods
    startResize(event) {
      this.resizeMode = 'both';
      this._startResize(event);
    },

    startResizeWidth(event) {
      this.resizeMode = 'width';
      this._startResize(event);
    },

    startResizeHeight(event) {
      this.resizeMode = 'height';
      this._startResize(event);
    },

    _startResize(event) {
      this.isResizing = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startWidth = this.panelWidth;
      this.startHeight = this.panelHeight;
      
      document.addEventListener('mousemove', this.doResize);
      document.addEventListener('mouseup', this.stopResize);
      event.preventDefault();
    },

    doResize(event) {
      if (!this.isResizing) return;
      
      // Panel zit rechtsboven, dus naar links slepen = breder, naar beneden = hoger
      const deltaX = this.startX - event.clientX;
      const deltaY = event.clientY - this.startY;
      
      if (this.resizeMode === 'width' || this.resizeMode === 'both') {
        this.panelWidth = Math.max(400, Math.min(1200, this.startWidth + deltaX));
      }
      if (this.resizeMode === 'height' || this.resizeMode === 'both') {
        this.panelHeight = Math.max(200, Math.min(800, this.startHeight + deltaY));
      }
    },

    stopResize() {
      this.isResizing = false;
      this.resizeMode = null;
      document.removeEventListener('mousemove', this.doResize);
      document.removeEventListener('mouseup', this.stopResize);
    }
  },

  mounted() {
    // Zorg dat er altijd een actieve tab is
    this.initializeActiveTab();
    
    // Herstel scroll positie als panel al open is
    if (this.isOpen) {
      this.restoreScrollPosition();
    }
  },

  beforeUnmount() {
    // Cleanup listeners
    document.removeEventListener('mousemove', this.doResize);
    document.removeEventListener('mouseup', this.stopResize);
    document.removeEventListener('mousemove', this.doDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  }
};
</script>

<style scoped>
.diagnostics-panel {
  position: absolute;
  top: 10px;
  right: 10px;
  width: auto;
  max-width: calc(100vw - 320px);
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

.diagnostics-panel:not(.panel-open) {
  width: auto;
  height: auto;
}

.panel-header {
  padding: 0.5rem 1rem;
  cursor: grab;
  user-select: none;
  background-color: rgba(233, 236, 239, 0.95);
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #dee2e6;
}

.panel-header:active {
  cursor: grabbing;
}

.is-dragging {
  opacity: 0.9;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.panel-open .panel-header {
  border-radius: 8px 8px 0 0;
}

.diagnostics-panel:not(.panel-open) .panel-header {
  border-radius: 8px;
  border-bottom: none;
}

.panel-header:hover {
  background-color: rgba(222, 226, 230, 0.95);
}

.panel-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.panel-content {
  padding: 0.75rem 1rem;
  padding-left: 1.5rem; /* Ruimte voor resize handle */
  padding-bottom: 1.5rem; /* Ruimte voor resize handle */
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.resize-handle-left {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 12px;
  width: 8px;
  cursor: ew-resize;
  background: linear-gradient(90deg, #dee2e6 0%, transparent 100%);
  opacity: 0.5;
  transition: opacity 0.2s;
}

.resize-handle-left:hover {
  opacity: 1;
  background: linear-gradient(90deg, #6c757d 0%, transparent 100%);
}

.resize-handle-bottom {
  position: absolute;
  left: 12px;
  right: 0;
  bottom: 0;
  height: 8px;
  cursor: ns-resize;
  background: linear-gradient(180deg, transparent 0%, #dee2e6 100%);
  opacity: 0.5;
  transition: opacity 0.2s;
}

.resize-handle-bottom:hover {
  opacity: 1;
  background: linear-gradient(180deg, transparent 0%, #6c757d 100%);
}

.resize-handle-corner {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 12px;
  height: 12px;
  cursor: nesw-resize;
  background: #dee2e6;
  opacity: 0.5;
  transition: opacity 0.2s;
  border-radius: 0 4px 0 0;
}

.resize-handle-corner:hover {
  opacity: 1;
  background: #6c757d;
}

.resize-handle-corner::after {
  content: '';
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 6px;
  height: 6px;
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
}

.section-title {
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
  color: #495057;
}

.table-container {
  max-height: 100%;
  overflow-y: auto;
  flex: 1;
}

.table {
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
}

.raw-data-box {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.75rem;
}

.raw-data-box pre {
  font-size: 0.7rem;
  max-height: 60px;
  overflow-y: auto;
}

.ids-list {
  margin-top: 0.25rem;
}

.badge {
  font-size: 0.7rem;
}

.warnings-section {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.field-name {
  font-size: 0.75rem;
  background-color: #f8f9fa;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
}

/* Toggle row styling */
.overlap-table {
  margin-bottom: 0;
}

.overlap-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.overlap-row:hover {
  background-color: rgba(0, 123, 255, 0.08) !important;
}

.overlap-row.expanded {
  background-color: rgba(0, 123, 255, 0.05) !important;
}

.toggle-cell {
  text-align: center;
  padding: 0.25rem !important;
}

.toggle-icon {
  font-size: 0.65rem;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.toggle-icon.rotated {
  transform: rotate(90deg);
}

.expanded-row {
  background-color: #f8f9fa !important;
}

.expanded-content {
  padding: 0.5rem 0.75rem !important;
  border-top: none;
}

.expanded-content .ids-list {
  margin-top: 0;
}

.raw-data-pre {
  font-size: 0.7rem;
  max-height: 80px;
  overflow-y: auto;
  background-color: #fff;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

/* Tabs styling */
.diagnostics-tabs {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;
}

.diagnostics-tabs .nav-item {
  margin-bottom: -1px;
}

.diagnostics-tabs .nav-link {
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  color: #6c757d;
  border: 1px solid transparent;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  cursor: pointer;
  background: none;
}

.diagnostics-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
  background-color: #f8f9fa;
}

.diagnostics-tabs .nav-link.active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  font-weight: 500;
}

.diagnostics-tabs .nav-link .badge {
  font-size: 0.65rem;
  padding: 0.2em 0.5em;
}

</style>
